function _extends() {
  _extends = Object.assign || function(target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef} from "react";
import {cmpClassName, execFn, getRandomString} from "../../../common/generic-react.functions.js";
import {useStateWithCallbackLazy} from "../../../hooks/state-with-callback.hook.js";
import Icon from "../icon/icon.component.js";
import Label from "../label/label.component.js";
import {useValidationInput} from "./input.hooks.js";
import {InputContainer, InputResetContainer} from "./input.styles.js";
function MainInput({
  append,
  appendIcon,
  bgColor,
  bold = "medium",
  color,
  disabled,
  hint,
  label,
  name,
  prepend,
  prependIcon,
  readOnly,
  required,
  reset,
  rules,
  size = "base",
  type = "text",
  value = "",
  width,
  placeholder,
  onBlur,
  onChange,
  onClickAppend,
  onClickPrepend,
  onFocus,
  onReset,
  ...otherProps
}, ref) {
  const [text, setText] = useStateWithCallbackLazy(value);
  const inputRef = useRef(null);
  const {
    errorMessage: [errorMessage],
    validateOnChange: [validateOnChange, setValidateOnChange],
    isValidInput
  } = useValidationInput({
    inputRef,
    rules
  });
  const inputId = useMemo(() => name || getRandomString(), [name]);
  useEffect(() => {
    setText(value);
  }, [setText, value]);
  const _onChange = useCallback((v) => {
    setText(v, (txt) => {
      execFn(onChange, txt);
    });
    if (validateOnChange) {
      isValidInput(true);
    }
  }, [isValidInput, onChange, setText, validateOnChange]);
  useImperativeHandle(ref, () => {
    return {
      el: inputRef.current,
      valid: (e) => {
        const valid = isValidInput(e);
        setValidateOnChange(!valid);
        return valid;
      },
      setValidateOnChange: (v = false) => setValidateOnChange(v),
      value: text,
      setValue: (v) => _onChange(v || "")
    };
  }, [_onChange, isValidInput, setValidateOnChange, text]);
  const resetValue = useCallback(() => {
    setText("", (txt) => {
      execFn(onChange, txt);
    });
    execFn(onReset, text);
    if (validateOnChange) {
      isValidInput(true);
    }
  }, [isValidInput, onChange, onReset, setText, text, validateOnChange]);
  const hasReset = useMemo(() => !!reset && !!text, [reset, text]);
  return /* @__PURE__ */ React.createElement(InputContainer, {
    hasReset,
    className: cmpClassName("input"),
    errorMessage,
    size,
    width,
    disabled: !!disabled,
    readOnly: !!readOnly,
    color,
    bgColor
  }, label && /* @__PURE__ */ React.createElement(Label, {
    bold,
    color,
    htmlFor: inputId,
    disabled,
    required
  }, label), /* @__PURE__ */ React.createElement("div", {
    className: "input-container"
  }, prependIcon && /* @__PURE__ */ React.createElement(Icon, {
    className: "input-container__prepend",
    name: prependIcon,
    onClick: onClickPrepend
  }), prepend, hasReset && /* @__PURE__ */ React.createElement(InputResetContainer, null, /* @__PURE__ */ React.createElement(Icon, {
    name: "backspace",
    onClick: resetValue
  })), /* @__PURE__ */ React.createElement("input", _extends({}, otherProps, {
    type,
    ref: inputRef,
    id: inputId,
    name,
    value: text || "",
    placeholder,
    required,
    disabled,
    readOnly,
    onFocus: () => execFn(onFocus, text || ""),
    onBlur: () => execFn(onBlur, text || ""),
    onChange: (e) => _onChange(e.target.value)
  })), append, appendIcon && /* @__PURE__ */ React.createElement(Icon, {
    className: "input-container__append",
    name: appendIcon,
    onClick: onClickAppend
  })), errorMessage && /* @__PURE__ */ React.createElement("div", {
    className: "input__error"
  }, errorMessage), hint && /* @__PURE__ */ React.createElement("div", {
    className: "input__hint"
  }, hint));
}
const Input = /* @__PURE__ */ React.memo(/* @__PURE__ */ forwardRef(MainInput));
Input.displayName = "Input";
export default Input;
